// src/pages/Financial/Payments.js

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Receipt as ReceiptIcon,
  CreditCard as PaymentIcon,
  AccountBalance as BankIcon,
  Money as CashIcon,
  Payment as CardIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import apiClient from '../../api/client';
import { formatCurrency as formatCurrencyUtil } from '../../utils/currency';
import DevOnly from '../../components/DevOnly';

const PaymentsManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');
  const [dateRange, setDateRange] = useState('this_month');
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  // Fetch payments data
  const { data: paymentsData, isLoading, error } = useQuery({
    queryKey: ['payments', searchTerm, methodFilter, dateRange, paginationModel],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        params.append('skip', String(paginationModel.page * paginationModel.pageSize));
        params.append('limit', String(paginationModel.pageSize));
        if (searchTerm) params.append('search', searchTerm);
        if (methodFilter !== 'all') params.append('method', methodFilter);
        if (dateRange !== 'all') params.append('date_range', dateRange);
        
        const response = await apiClient.get(`/financial/payments?${params.toString()}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching payments:', error);
        return { payments: [], total: 0, stats: {} };
      }
    },
    staleTime: 2 * 60 * 1000,
  });

  const formatCurrency = (amount) => formatCurrencyUtil(amount);

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method?.toLowerCase()) {
      case 'credit_card':
      case 'debit_card':
        return <CardIcon fontSize="small" />;
      case 'bank_transfer':
        return <BankIcon fontSize="small" />;
      case 'cash':
        return <CashIcon fontSize="small" />;
      default:
        return <PaymentIcon fontSize="small" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'refunded':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleRefundClick = (payment) => {
    setSelectedPayment(payment);
    setRefundDialogOpen(true);
  };

  const handleRefundClose = () => {
    setRefundDialogOpen(false);
    setSelectedPayment(null);
  };

  const columns = [
    {
      field: 'payment_id',
      headerName: 'Payment ID',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value || `PAY-${params.row.id?.slice(-6)}`}
        </Typography>
      )
    },
    {
      field: 'payment_date',
      headerName: 'Date',
      width: 120,
      renderCell: (params) => formatDate(params.value)
    },
    {
      field: 'client_name',
      headerName: 'Client',
      width: 200,
      renderCell: (params) => params.value || 'Unknown Client'
    },
    {
      field: 'invoice_number',
      headerName: 'Invoice',
      width: 120,
      renderCell: (params) => (
        params.value ? (
          <Chip
            label={params.value}
            size="small"
            onClick={() => navigate(`/financial/invoices/${params.row.invoice_id}`)}
            clickable
          />
        ) : '-'
      )
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {formatCurrency(params.value)}
        </Typography>
      )
    },
    {
      field: 'payment_method',
      headerName: 'Method',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {getPaymentMethodIcon(params.value)}
          <Typography variant="body2">
            {params.value?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value || 'Pending'}
          color={getStatusColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'fee_amount',
      headerName: 'Fees',
      width: 100,
      renderCell: (params) => params.value ? formatCurrency(params.value) : '-'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => navigate(`/financial/payments/${params.row.id}`)}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Receipt">
            <IconButton
              size="small"
              onClick={() => console.log('View receipt:', params.row.id)}
            >
              <ReceiptIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status === 'completed' && (
            <DevOnly>
              <Tooltip title="Refund">
                <Button
                  size="small"
                  onClick={() => handleRefundClick(params.row)}
                >
                  Refund
                </Button>
              </Tooltip>
            </DevOnly>
          )}
        </Box>
      )
    }
  ];

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load payments. Please try again later.
        </Alert>
      </Box>
    );
  }

  const stats = paymentsData?.stats || {
    total_payments: 0,
    total_amount: 0,
    average_payment: 0,
    payment_methods: {},
    yearAmount: 0,
    methodBreakdown: {}
  };

  const payments = paymentsData?.payments || [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Payments
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track and manage all payment transactions
          </Typography>
        </Box>
        <DevOnly>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/financial/payments/record')}
            size="large"
          >
            Record Payment
          </Button>
        </DevOnly>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Total Collected
              </Typography>
              <Typography variant="h5" component="div">
                {formatCurrency(stats.total_amount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {paymentsData?.total || 0} payments
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Today
              </Typography>
              <Typography variant="h5" component="div" color="success.main">
                {formatCurrency(stats.todayAmount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDistanceToNow(new Date().setHours(0, 0, 0, 0), { addSuffix: true })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                This Month
              </Typography>
              <Typography variant="h5" component="div">
                {formatCurrency(stats.monthAmount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {new Date().toLocaleDateString('en-US', { month: 'long' })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Average Payment
              </Typography>
              <Typography variant="h5" component="div">
                {formatCurrency(stats.average_payment)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Per transaction
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Payment Methods Breakdown */}
      {stats.methodBreakdown && Object.keys(stats.methodBreakdown).length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Payment Methods
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(stats.methodBreakdown).map(([method, data]) => (
              <Grid item xs={12} sm={6} md={3} key={method}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getPaymentMethodIcon(method)}
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatCurrency(data.amount)} ({data.count} payments)
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search by payment ID, client, invoice..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, maxWidth: 400 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Payment Method</InputLabel>
          <Select
            value={methodFilter}
            label="Payment Method"
            onChange={(e) => setMethodFilter(e.target.value)}
          >
            <MenuItem value="all">All Methods</MenuItem>
            <MenuItem value="credit_card">Credit Card</MenuItem>
            <MenuItem value="debit_card">Debit Card</MenuItem>
            <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Date Range</InputLabel>
          <Select
            value={dateRange}
            label="Date Range"
            onChange={(e) => setDateRange(e.target.value)}
          >
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="this_week">This Week</MenuItem>
            <MenuItem value="this_month">This Month</MenuItem>
            <MenuItem value="last_month">Last Month</MenuItem>
            <MenuItem value="this_year">This Year</MenuItem>
            <MenuItem value="all">All Time</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
        >
          Export
        </Button>
      </Paper>

      {/* Payments DataGrid */}
      <Paper>
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={payments}
            columns={columns}
            rowCount={paymentsData?.total || 0}
            pageSizeOptions={[25, 50, 100]}
            paginationModel={paginationModel}
            paginationMode="server"
            onPaginationModelChange={setPaginationModel}
            checkboxSelection
            disableSelectionOnClick
            loading={isLoading}
            getRowId={(row) => row.id}
            sx={{
              '& .MuiDataGrid-root': {
                border: 'none',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'grey.50',
              },
            }}
          />
        </Box>
      </Paper>

      {/* Refund Dialog */}
      <Dialog open={refundDialogOpen} onClose={handleRefundClose}>
        <DialogTitle>Process Refund</DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Box>
              <Typography variant="body2" gutterBottom>
                Are you sure you want to refund this payment?
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Payment ID: {selectedPayment.payment_id || `PAY-${selectedPayment.id?.slice(-6)}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Amount: {formatCurrency(selectedPayment.amount)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Client: {selectedPayment.client_name}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRefundClose}>Cancel</Button>
          <Button onClick={handleRefundClose} variant="contained" color="error">
            Process Refund
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentsManagement;